<template>
  <v-container class="ma-0 pa-2 fill-height">
    <v-container class="d-flex pa-0 ma-0">
      <div class="h5 font-weight-bold">Esecuzione viaggio</div>
    </v-container>

    <v-row class="fill-height" no-gutters>
      <!-- LEFT: Info Columns + Timeline -->
      <v-col cols="12" md="6" class="pr-2 d-flex flex-column">
        <!-- Info Card -->
        <v-card class="pa-3" outlined flat>
          <v-card-text class="d-flex flex-row pa-0">
            <div class="flex-grow-1 pr-2 d-flex flex-column">
              <div class="mb-5 d-flex align-center">
                <v-icon class="mr-1" small>mdi-xml</v-icon>
                <span>{{ missionPlan?.trackingCode || '' }}</span>
              </div>
              <div class="mb-5 d-flex align-center">
                <v-icon class="mr-1" small>mdi-xml</v-icon>
                <span>{{ missionPlan?.code || '' }}</span>
              </div>
              <div class="mb-5 d-flex align-center">
                <v-icon class="mr-1" small>mdi-truck</v-icon>
                <span>{{ tractor?.plate || '' }} {{ tractor?.plateCountry.code || '' }}</span>
              </div>
              <div class="mb-5 d-flex align-center">
                <v-icon class="mr-1" small>mdi-calendar-start</v-icon>
                <span>{{ startTime | formatDate }}</span>
              </div>
            </div>

            <div class="flex-grow-1 pl-2 d-flex flex-column">
              <div class="mb-5 d-flex align-center">
                <v-icon class="mr-1" small>mdi-racing-helmet</v-icon>
                <span>{{ driver?.firstName || '' }} {{ driver?.lastName || '' }}</span>
              </div>
              <div class="mb-5 d-flex align-center">
                <v-icon class="mr-1" small>mdi-truck</v-icon>
                <span>{{ trailer?.plate || '' }} {{ trailer?.plateCountry.code || '' }}</span>
              </div>
              <div class="mb-5 d-flex align-center">
                <v-icon class="mr-1" small>mdi-calendar-end</v-icon>
                <span>{{ endTime | formatDate }}</span>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Timeline -->
        <v-card class="mt-4 pa-4" outlined flat v-if="missionEventsBrowseResult">
          <v-timeline>
            <!-- Start -->
            <v-timeline-item color="green">
              <template v-slot:opposite>
                <strong>Inizio</strong>
              </template>
              <div v-for="(event) in [missionEventsBrowseResult[0]]" :key="event?.id || 'new-event'">
                <v-btn
                    v-if="!event"
                    class="primary"
                    @click="startMissionExecution"
                    depressed
                    x-small
                >
                  Inizio missione
                </v-btn>
                <div class="d-flex" v-else>
                  {{ formatDate(event.eventTime) }}
                  <v-icon class="mr-1" @click="centerMapAndZoom('map', event.latitude, event.longitude)">
                    mdi-map-marker
                  </v-icon>
                  <span v-if="event.user" class="d-flex flex-column user-info">
                    <span class="label">Creato da</span>
                    <span>{{ event.user.firstName }} {{ event.user.lastName }}</span>
                  </span>
                </div>
              </div>
            </v-timeline-item>

            <!-- Activities -->
            <v-timeline-item
                v-for="(activity, i) in missionPlan.activities"
                :key="activity.id"
                right
                small
                :color="getColor(activity)"
                class="mb-4"
            >
              <template v-slot:opposite>
                <strong>{{ activity.type }}</strong>
                <div v-if="activity.address">
                  <v-icon class="mr-1" small>mdi-map-marker</v-icon>
                  <span>{{ getAddress(activity.address) }}</span>
                </div>
                <div v-else>
                  <v-icon class="mr-1" small>mdi-map-marker</v-icon>
                  <span>Indirizzo non disponibile</span>
                </div>
                <div>
                  <v-icon class="mr-1" small>mdi-calendar-start</v-icon>
                  <span>{{
                      activity.expectedBeginTime ? formatDate(activity.expectedBeginTime) : 'Nessuna data prevista'
                    }}</span>
                </div>
                <div>
                  <v-icon class="mr-1" small>mdi-calendar-end</v-icon>
                  <span>{{
                      activity.expectedEndTime ? formatDate(activity.expectedEndTime) : 'Nessuna data prevista'
                    }}</span>
                </div>
              </template>

              <div
                  v-for="event in missionEventsBrowseResult.filter(e => e.activityId === activity.id)"
                  :key="event.id"
                  class="d-flex align-center mb-2"
              >
                {{ formatDate(event.eventTime) }}
                <v-icon class="ml-2 mr-1" small @click="centerMapAndZoom('map', event.latitude, event.longitude)">
                  mdi-map-marker
                </v-icon>
                <span v-if="event.user" class="d-flex flex-column user-info ml-2">
                  <span class="label">Creato da</span>
                  <span>{{ event.user.firstName }} {{ event.user.lastName }}</span>
                </span>
              </div>

              <v-btn
                  class="primary"
                  v-if="activityStartEnabled(i)"
                  @click="startMissionActivity(activity)"
                  depressed
                  x-small
              >
                Inizio attività
              </v-btn>

              <v-btn
                  v-else-if="activityEndEnabled(activity.id)"
                  class="primary"
                  @click="endMissionActivity(activity)"
                  depressed
                  x-small
              >
                Fine attività
              </v-btn>
            </v-timeline-item>

            <!-- End -->
            <v-timeline-item color="green" right class="align-center">
              <template v-slot:opposite>
                <strong>Fine</strong>
              </template>

              <template v-if="executionEndEnabled()">
                <v-btn
                    class="primary"
                    @click="endMissionExecution"
                    depressed
                    x-small
                >
                  Fine missione
                </v-btn>
              </template>
              <template
                  v-else-if="missionEventsBrowseResult.length > 1 && !missionEventsBrowseResult.at(-1).activityId"
              >
                <div class="d-flex">
                  {{ formatDate(missionEventsBrowseResult.at(-1).eventTime) }}
                  <v-icon
                      class="mr-1"
                      @click="centerMapAndZoom('map', missionEventsBrowseResult.at(-1).latitude, missionEventsBrowseResult.at(-1).longitude)"
                  >
                    mdi-map-marker
                  </v-icon>
                  <span v-if="missionEventsBrowseResult.at(-1).user" class="d-flex flex-column user-info">
                    <span class="label">Creato da</span>
                    <span>{{
                        missionEventsBrowseResult.at(-1).user.firstName
                      }} {{ missionEventsBrowseResult.at(-1).user.lastName }}</span>
                  </span>
                </div>
              </template>
            </v-timeline-item>
          </v-timeline>
        </v-card>
      </v-col>

      <!-- RIGHT: MAP -->
      <v-col cols="12" md="6" class="pl-2">
        <v-card class="pa-4 fill-height d-flex flex-column">
          <MapContainerDemo :transportOrderNbr="this.transportOrder.transportOrderNbr" ref="map" style="flex: 1;"/>
          <v-card-text>
            <div class="d-flex flex-wrap mx-n2">
              <!-- Events block -->
              <div class="mx-2 d-flex flex-column">
                <span class="font-weight-bold">Eventi - Ultima posizione</span>

                <v-chip
                    v-if="latestPositions.events"
                    label
                    small
                    color="primary"
                    text-color="white"
                    @click="centerMapAndZoom('map', latestPositions.events.latitude, latestPositions.events.longitude)"
                >
                  <v-avatar>
                    <v-icon small>mdi-map-marker</v-icon>
                  </v-avatar>
                  {{ latestPositions.events.latitude }}, {{ latestPositions.events.longitude }}
                  <v-avatar>
                    <v-icon small>mdi-clock</v-icon>
                  </v-avatar>
                  {{ (latestPositions.events.positionTime || latestPositions.events.eventTime) | formatDate }}
                </v-chip>

                <v-chip v-else label small color="#cecece">
                  Nessuna posizione disponibile
                </v-chip>
              </div>

              <!-- GPS block -->
              <div class="mx-2 d-flex flex-column">
                <span class="font-weight-bold">GPS - Ultima posizione</span>

                <v-chip
                    v-if="latestPositions.GPS"
                    label
                    small
                    color="primary"
                    text-color="white"
                    @click="centerMapAndZoom('map', latestPositions.GPS.latitude, latestPositions.GPS.longitude)"
                >
                  <v-avatar>
                    <v-icon small>mdi-map-marker</v-icon>
                  </v-avatar>
                  {{ latestPositions.GPS.latitude }}, {{ latestPositions.GPS.longitude }}
                  <v-avatar>
                    <v-icon small>mdi-clock</v-icon>
                  </v-avatar>
                  {{ (latestPositions.GPS.positionTime || latestPositions.GPS.eventTime) | formatDate }}
                </v-chip>

                <v-chip v-else label small color="#cecece">
                  Nessuna posizione disponibile
                </v-chip>
              </div>
            </div>

          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import MapContainerDemo from "./MapContainerDemo";

export default {
  components: {MapContainerDemo},
  props: ['transportOrder'],
  data() {
    return {
      missionEventsBrowseResult: [],
      missionScenarios: {
        notStarted: [],
        ongoing: [
          {
            id: 1,
            type: 'BEGIN',
            eventTime: new Date('2025-06-23T08:00:00'),
            activityId: null,
            latitude: 45.4642,
            longitude: 9.19,
            user: {firstName: 'Luca', lastName: 'Rossi'}
          },
          {
            id: 2,
            type: 'BEGIN_ACTIVITY',
            activityId: 1,
            eventTime: new Date('2025-06-23T08:15:00'),
            latitude: 45.0703,
            longitude: 7.6869,
            user: {firstName: 'Giulia', lastName: 'Bianchi'}
          },
          {
            id: 3,
            type: 'END_ACTIVITY',
            activityId: 1,
            eventTime: new Date('2025-06-23T08:45:00'),
            latitude: 45.0703,
            longitude: 7.6870,
            user: {firstName: 'Giulia', lastName: 'Bianchi'}
          },
          {
            id: 4,
            type: 'BEGIN_ACTIVITY',
            activityId: 2,
            eventTime: new Date('2025-06-23T09:00:00'),
            latitude: 44.4949,
            longitude: 11.3426,
            user: {firstName: 'Marco', lastName: 'Verdi'}
          }
        ],
        completed: [
          {
            id: 1,
            type: 'BEGIN',
            eventTime: new Date('2025-06-23T08:00:00'),
            activityId: null,
            latitude: 45.4642,
            longitude: 9.19,
            user: {firstName: 'Luca', lastName: 'Rossi'}
          },
          {
            id: 2,
            type: 'BEGIN_ACTIVITY',
            activityId: 1,
            eventTime: new Date('2025-06-23T08:15:00'),
            latitude: 45.0703,
            longitude: 7.6869,
            user: {firstName: 'Giulia', lastName: 'Bianchi'}
          },
          {
            id: 3,
            type: 'END_ACTIVITY',
            activityId: 1,
            eventTime: new Date('2025-06-23T08:45:00'),
            latitude: 45.0703,
            longitude: 7.6870,
            user: {firstName: 'Giulia', lastName: 'Bianchi'}
          },
          {
            id: 4,
            type: 'BEGIN_ACTIVITY',
            activityId: 2,
            eventTime: new Date('2025-06-23T09:00:00'),
            latitude: 44.4949,
            longitude: 11.3426,
            user: {firstName: 'Marco', lastName: 'Verdi'}
          },
          {
            id: 5,
            type: 'END_ACTIVITY',
            activityId: 2,
            eventTime: new Date('2025-06-23T09:30:00'),
            latitude: 44.4949,
            longitude: 11.3430,
            user: {firstName: 'Marco', lastName: 'Verdi'}
          },
          {
            id: 6,
            type: 'END',
            eventTime: new Date('2025-06-23T10:00:00'),
            activityId: null,
            latitude: 45.4408,
            longitude: 12.3155,
            user: {firstName: 'Luca', lastName: 'Rossi'}
          }
        ],
        completedGenovaToMilan: [
          {
            id: 1,
            type: 'BEGIN',
            eventTime: new Date('2025-07-21T09:30:00'),
            activityId: null,
            latitude: 44.4056,
            longitude: 8.9462,
            user: {firstName: 'Luca', lastName: 'Rossi'}
          },
          {
            id: 2,
            type: 'BEGIN_ACTIVITY',
            activityId: 1,
            eventTime: new Date('2025-07-21T10:30:00'),
            latitude: 44.4056,
            longitude: 8.9462,
            user: {firstName: 'Giulia', lastName: 'Bianchi'}
          },
          {
            id: 3,
            type: 'END_ACTIVITY',
            activityId: 1,
            eventTime: new Date('2025-07-21T11:35:00'),
            latitude: 44.3845,
            longitude: 9.0603,
            user: {firstName: 'Giulia', lastName: 'Bianchi'}
          },
          {
            id: 4,
            type: 'BEGIN_ACTIVITY',
            activityId: 2,
            eventTime: new Date('2025-07-21T11:55:00'),
            latitude: 44.8944,
            longitude: 8.8824,
            user: {firstName: 'Marco', lastName: 'Verdi'}
          },
          {
            id: 5,
            type: 'END_ACTIVITY',
            activityId: 2,
            eventTime: new Date('2025-07-21T12:25:00'),
            latitude: 45.5237,
            longitude: 9.2067,
            user: {firstName: 'Marco', lastName: 'Verdi'}
          },
          {
            id: 6,
            type: 'END',
            eventTime: new Date('2025-07-21T13:24:00'),
            activityId: null,
            latitude: 45.5237,
            longitude: 9.2067,
            user: {firstName: 'Luca', lastName: 'Rossi'}
          }
        ]
      },
      missionPlan: {
        trackingCode: 'TR1234567',
        code: 'MP-2025-001',
        activities: [
          {
            id: 1,
            type: 'Ritiro pieno',
            address: {
              street: 'VIALE DELLE PIAGGE',
              city: 'GENOVA',
              zip: '16129',
              country: 'IT'
            },
            expectedBeginTime: new Date('2025-07-21T07:00:00'),
            expectedEndTime: new Date('2025-07-21T07:45:00')
          },
          {
            id: 2,
            type: 'Consegna pieno',
            address: {
              street: 'VIA DI VITTORIO',
              city: 'MILAN',
              zip: '20099',
              country: 'IT'
            },
            expectedBeginTime: null,
            expectedEndTime: null
          }
        ]
      },
      driver: {
        firstName: 'Alex',
        lastName: 'Smith'
      },
      tractor: {
        plate: 'AB123CD',
        plateCountry: {
          code: 'DE'
        }
      },
      trailer: {
        plate: 'TR456EF',
        plateCountry: {
          code: 'FR'
        }
      },
      startTime: new Date(Date.now() - 3600 * 1000 * 24),
      endTime: new Date(),
      latestPositions: {
        events: {
          latitude: 45.12345,
          longitude: 9.54321,
          positionTime: '2025-06-23T07:00:00Z'
        },
        GPS: {
          latitude: 44.98765,
          longitude: 10.12345,
          positionTime: '2025-06-23T07:10:00Z'
        }
      },
    };
  },
  created() {
    switch (this.transportOrder.transportOrderNbr) {
      case '7003592144':
        this.missionEventsBrowseResult = [...this.missionScenarios['completed']];
        break;
      case '7003591008':
        this.missionEventsBrowseResult = [...this.missionScenarios['ongoing']];
        break;
      case '7003570762':
        this.missionEventsBrowseResult = [...this.missionScenarios['notStarted']];
        break;
      case '7013037526':
        this.missionEventsBrowseResult = [...this.missionScenarios['completedGenovaToMilan']];
        break;
    }
  },
  methods: {
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleString();
    },
    getColor(activity) {
      if (!activity || !this.missionEventsBrowseResult) return 'white';

      const exactMatch = this.missionEventsBrowseResult.find(event =>
          event.activityId === activity.id &&
          activity.expectedEndTime &&
          new Date(event.eventTime).getTime() === new Date(activity.expectedEndTime).getTime()
      );

      if (exactMatch) return 'green lighten-3';

      const hasAnyEvent = this.missionEventsBrowseResult.some(event => event.activityId === activity.id);
      return hasAnyEvent ? 'yellow' : 'white';
    },
    getAddress(address) {
      if (!address) return '<span class="font-italic">No address available</span>';

      const name = address.name ? `<span class="font-weight-bold">${address.name}</span> ` : '';
      const street = address.street ? `${address.street} ` : '';
      const number = address.number ? `${address.number}, ` : '';
      const city = address.city ? `${address.city}, ` : '';
      const postal = address.postalCode ? `${address.postalCode} ` : '';
      const country = address.countryCode || '';

      return `${name}${street}${number}${city}${postal}${country}`.trim();
    },
    activityStartEnabled: function (activityIndex) {
      if (!this.missionEventsBrowseResult.length) {
        return false;
      }
      const previousActivity = (this.missionPlan?.activities || [])[activityIndex - 1];
      const lastEvent = this.missionEventsBrowseResult.at(-1);
      if (!previousActivity) {
        return lastEvent?.type === 'BEGIN';
      } else {
        return lastEvent?.activityId === previousActivity?.id && lastEvent?.type === 'END_ACTIVITY';
      }
    },
    activityEndEnabled: function (activityId) {
      if (!this.missionEventsBrowseResult.length) {
        return false;
      }
      const lastEvent = this.missionEventsBrowseResult.at(-1);
      return lastEvent?.activityId === activityId && lastEvent?.type === 'BEGIN_ACTIVITY';
    },
    startMissionExecution: function () {
      this.selectedItem = {
        type: 'BEGIN',
        latitude: 45.4642,
        longitude: 9.19,
      };
      this.createMissionEvent('START_MISSION');
    },
    endMissionExecution() {
      this.selectedItem = {
        type: 'END',
        latitude: this.missionPlan.arrivalPlace?.latitude || 45.0,
        longitude: this.missionPlan.arrivalPlace?.longitude || 9.0
      };
      this.createMissionEvent('END_MISSION');
    },
    startMissionActivity(activity) {
      this.selectedItem = {
        type: 'BEGIN_ACTIVITY',
        activityId: activity.id,
        latitude: activity.address?.latitude || 45.0,
        longitude: activity.address?.longitude || 9.0
      };

      this.manageOrderReferenceData(activity);
      this.createMissionEvent('BEGIN_ACTIVITY');
    },
    endMissionActivity(activity) {
      this.selectedItem = {
        type: 'END_ACTIVITY',
        activityId: activity.id,
        latitude: activity.address?.latitude || 45.0,
        longitude: activity.address?.longitude || 9.0
      };

      this.manageOrderReferenceData(activity);
      this.createMissionEvent('END_ACTIVITY');
    },
    createMissionEvent(action) {
      const typeMap = {
        startMission: 'START_MISSION',
        startActivity: 'BEGIN_ACTIVITY',
        endActivity: 'END_ACTIVITY'
      };

      const event = {
        id: this.missionEventsBrowseResult.length + 100,
        type: typeMap[action],
        activityId: this.selectedItem.activityId,
        eventTime: new Date(),
        latitude: this.selectedItem.latitude,
        longitude: this.selectedItem.longitude,
        user: {firstName: 'Demo', lastName: 'User'}
      };

      this.missionEventsBrowseResult.push(event);
      if (action === 'START_MISSION') {
        this.$refs.map.addMarker(9.19, 45.4642, 'Manual Start Point');
      }
    },
    manageOrderReferenceData(activity) {
      // no-op for demo
    },
    centerMapAndZoom(mapRef, lat, lng, zoom = 14) {
      if (lat && lng) {
        console.log(`[Demo] Centering map '${mapRef}' on (${lat}, ${lng}) with zoom ${zoom}`);

        // Optional: Show a message in the UI
        this.lastMapAction = `Map centered on (${lat.toFixed(4)}, ${lng.toFixed(4)}) at zoom ${zoom}`;

        // Optional: simulate ref object for mock map
        if (this.$refs[mapRef]) {
          this.$refs[mapRef].setMapCenter?.(lat, lng);
          this.$refs[mapRef].setMapZoom?.(zoom);
        }
      }
    },
    executionEndEnabled() {
      if (!this.missionEventsBrowseResult.length) {
        return false;
      }
      const lastEvent = this.missionEventsBrowseResult.at(-1);
      const lastActivity = this.missionPlan?.activities?.at(-1);
      return lastEvent?.activityId === lastActivity?.id && lastEvent?.type === 'END_ACTIVITY';
    },
  }
};
</script>

<style>

.user-info {
  font-size: 12px;
  line-height: 11px;
}

.label {
  font-size: 10px;
  font-weight: bold;
}

</style>